import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useQuery } from 'react-query';

import {
  FuelReading,
  FuelRecordKind,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import {
  SLOT_LABEL,
  formatDateTime,
  formatDay,
  formatLiters,
  formatReading,
} from '../../../utils/fuelReadings';
import { AttachPhotoButton } from './AttachPhotoButton';

const CALIBRATIONS_KEY = ['fuel-readings', 'latest-calibrations'];
const RECEIPTS_KEY = ['fuel-readings', 'latest-receipts'];

interface Missing {
  kind: FuelRecordKind;
  id: string;
  at: string;
  text: ReactNode;
}

// lançamentos recentes sem foto (leitura feita no pc do posto, aferição ou
// nota lançadas sem foto): o frentista anexa quando tiver sinal no celular.
// a api devolve os últimos de cada um para quem só lança.
export function MissingPhotos({ readings }: { readings: FuelReading[] }) {
  const calibrations = useQuery(CALIBRATIONS_KEY, () =>
    fuelReadingsService.calibrations({ limit: 10 }),
  );
  const receipts = useQuery(RECEIPTS_KEY, () =>
    fuelReadingsService.receipts({ limit: 10 }),
  );

  const missing: Missing[] = [
    ...readings
      .filter(
        item => !item.photo_url && !item.canceled_at && item.reading !== null,
      )
      .map(item => ({
        kind: 'reading' as const,
        id: item.id,
        at: item.read_at,
        text: (
          <>
            Leitura · {item.pump_name} · {SLOT_LABEL[item.slot].toLowerCase()} ·{' '}
            {formatDateTime(item.read_at)} ·{' '}
            <Text as="span" fontFamily="mono">
              {formatReading(item.reading)}
            </Text>
          </>
        ),
      })),
    ...(calibrations.data || [])
      .filter(item => !item.photo_url && !item.canceled_at)
      .map(item => ({
        kind: 'calibration' as const,
        id: item.id,
        at: item.calibrated_at,
        text: (
          <>
            Aferição · {item.pump_name} · {formatDateTime(item.calibrated_at)}
          </>
        ),
      })),
    ...(receipts.data || [])
      .filter(item => !item.photo_url && !item.canceled_at)
      .map(item => ({
        kind: 'receipt' as const,
        id: item.id,
        at: item.created_at,
        text: (
          <>
            Nota {item.document} · {formatLiters(item.liters)} L · descarga{' '}
            {formatDay(item.received_on).slice(0, 5)}
          </>
        ),
      })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  if (missing.length === 0) {
    return null;
  }

  return (
    <Box
      mb="5"
      p="3"
      bg="gray.800"
      borderRadius="12px"
      borderLeftWidth="4px"
      borderLeftColor="orange.400"
    >
      <Text fontWeight="medium" mb="2">
        Falta foto ({missing.length})
      </Text>
      <Stack spacing="2">
        {missing.map(item => (
          <Flex
            key={`${item.kind}-${item.id}`}
            justify="space-between"
            align="center"
            gridGap="2"
            fontSize="sm"
          >
            <Text>{item.text}</Text>
            <Box flexShrink={0}>
              <AttachPhotoButton kind={item.kind} id={item.id} />
            </Box>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
